import React, { useCallback, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import { OffersRoutes } from '../../../Router/path';
import theme from '../../../theme';
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard';
import AccordionCardWithDialogButton from '../../Shared/Components/AccordionCardWithDialogButton/AccordionCardWithDialogButton';
import { useCreateOfferMutation, useUpdateOfferMutation } from '../Service/offersApi';
import { useGetCategoriesQuery } from '../../Categories/Service/categoriesApi';
import { useGetTagsQuery } from '../../Tags/Service/tagsApi';
import { useGetCompaniesQuery } from '../../Companies/Service/companiesApi';
import { useGetOfferOptionTypesQuery } from '../../OfferOptions/Service/extendedOferOptionsApi';
import { useGetByOfferIdQuery } from '../../OfferOptionsMappings/Service/extendedOfferOptionsMappingsApi';
import OfferOptionMappingsSection from '../../OfferOptionsMappings/Components/OfferOptionMappingsSection/OfferOptionMappingsSection';
import OfferBoardingPlacesSection from '../../OfferBoardingPlaces/Components/OfferBoardingPlacesSection';
import OfferProgramTable from '../../OfferProgram/Components/OfferProgramTable';
import OfferBookingsTable from '../Components/OfferBooking/OfferBookingsTable';
import AutomaticBookingsDialog from '../Components/OfferBooking/AutomaticBookingsDialog';
import AddOfferLookupModal from '../Components/AddOfferLookupModal/AddOfferLookupModal';
import { offerValues } from '../initialValues';
import { offerFields } from '../fields';
import { Offer } from '../Types/offerTypes';
import { extractOfferEntityFormApiErrors } from '../Utils/offerEntityFormApiErrorHandling';
import { normalizeOfferForSubmit } from '../Utils/normalizeOfferForSubmit';
import {
  OFFER_LOOKUP_FIELDS,
  OfferLookupField,
  OfferLookupEntityOption,
  filterOfferLookupWithAddNew,
  mergeLookupOptions,
  NEW_OFFER_LOOKUP_OPTION_VALUE,
} from '../Utils/offerLookupAddNew';
import {
  useGetOfferLookupCountriesQuery,
  useGetOfferLookupHotelsQuery,
  useGetOfferLookupRegionsQuery,
} from '../Service/offersLocationLookupApi';

const CreateOfferPage = () => {
  const [createdOfferId, setCreatedOfferId] = useState<number | null>(null);
  const [showBookingsTable, setShowBookingsTable] = useState(false);
  const [autocompleteResetKeys, setAutocompleteResetKeys] = useState<Record<string, number>>({});
  const [addLookupModalOpen, setAddLookupModalOpen] = useState(false);
  const [activeLookupField, setActiveLookupField] = useState<OfferLookupField>('countryId');
  const [offerLookupSearches, setOfferLookupSearches] = useState<Record<OfferLookupField, string>>({
    countryId: '',
    regionId: '',
    hotelId: '',
  });
  const [additionalLookupOptions, setAdditionalLookupOptions] = useState<
    Record<OfferLookupField, OfferLookupEntityOption[]>
  >({
    countryId: [],
    regionId: [],
    hotelId: [],
  });

  const [createOffer, createStatus] = useCreateOfferMutation();
  const [updateOffer, updateStatus] = useUpdateOfferMutation();

  const { data: hotels, isLoading: hotelsLoading } = useGetOfferLookupHotelsQuery();
  const { data: regions, isLoading: regionsLoading } = useGetOfferLookupRegionsQuery();
  const { data: countries, isLoading: countriesLoading } = useGetOfferLookupCountriesQuery();
  const { data: categories, isLoading: categoriesLoading } = useGetCategoriesQuery();
  const { data: tags, isLoading: tagsLoading } = useGetTagsQuery();
  const { data: offerOptionTypes, isLoading: offerOptionsLoading } = useGetOfferOptionTypesQuery();
  const { data: companies, isLoading: companiesLoading } = useGetCompaniesQuery();
  const {
    data: mappings,
    isLoading: mappingsLoading,
    refetch: refetchMappings,
  } = useGetByOfferIdQuery(createdOfferId!, { skip: !createdOfferId });

  const hotelLookupOptions = useMemo(
    () =>
      mergeLookupOptions(hotels ?? [], additionalLookupOptions.hotelId),
    [hotels, additionalLookupOptions.hotelId]
  );

  const regionLookupOptions = useMemo(
    () => mergeLookupOptions(regions ?? [], additionalLookupOptions.regionId),
    [regions, additionalLookupOptions.regionId]
  );

  const countryLookupOptions = useMemo(
    () => mergeLookupOptions(countries ?? [], additionalLookupOptions.countryId),
    [countries, additionalLookupOptions.countryId]
  );

  const combinedMutationStatus = {
    isLoading: createStatus.isLoading || updateStatus.isLoading,
    isError: createStatus.isError || updateStatus.isError,
    isSuccess: createStatus.isSuccess || updateStatus.isSuccess,
    error: createStatus.error ?? updateStatus.error,
  };

  const entityForm = useEntityForm<Offer>({
    initialData: offerValues,
    fields: offerFields.map((field) => {
      if (field.type === 'autocomplete' && field.name === 'hotelId') {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: hotelLookupOptions,
          loading: hotelsLoading,
        };
      }

      if (field.type === 'autocomplete' && field.name === 'regionId') {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: regionLookupOptions,
          loading: regionsLoading,
        };
      }
      if (field.type === 'autocomplete' && field.name === 'countryId') {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: countryLookupOptions,
          loading: countriesLoading,
        };
      }
      if (field.type === 'autocomplete' && field.name === 'offerCategoryIds' && categories) {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: categories.map((category) => {
            const parent = categories.find((cat) => cat.id === category.parentCategoryId);
            return {
              label: parent ? `${parent.name} >>> ${category.name}` : category.name,
              value: category.id,
            };
          }),
          loading: categoriesLoading,
        };
      }
      if (field.type === 'autocomplete' && field.name === 'offerTagIds' && tags) {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: tags.map((tag) => ({
            label: tag.name,
            value: tag.id,
          })),
          loading: tagsLoading,
        };
      }

      if (field.type === 'autocomplete' && field.name === 'companyId' && companies) {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: companies.map((company) => ({
            label: company.name,
            value: company.id,
          })),
          loading: companiesLoading,
        };
      }
      return {
        ...field,
        name: field.name as keyof Offer,
      };
    }),
    submitFn: async (data) => {
      const normalizedData = normalizeOfferForSubmit(data);
      const { id, ...payload } = normalizedData;

      if (createdOfferId !== null) {
        return await updateOffer({ id: createdOfferId, data: normalizedData }).unwrap();
      }

      const createResult = await createOffer(payload).unwrap();
      const newOfferId = createResult?.id;
      const isValidNewOfferId =
        typeof newOfferId === 'number' && Number.isInteger(newOfferId) && newOfferId > 0;

      if (!isValidNewOfferId) {
        throw new Error('Nie udało się zapisać oferty. Brak poprawnego identyfikatora.');
      }

      setCreatedOfferId(newOfferId);
      return await updateOffer({ id: newOfferId, data: normalizedData }).unwrap();
    },
    redirectUrl: OffersRoutes.root,
    title: 'Utwórz ofertę',
    mutationStatus: combinedMutationStatus,
    errorExtractor: extractOfferEntityFormApiErrors,
    groupOrder: [
      FormRenderGroupTypes.basic,
      FormRenderGroupTypes.additionalInformation,
      FormRenderGroupTypes.seo,
      FormRenderGroupTypes.configuration,
    ],
    groupIcons: {
      Podstawowe: <InfoIcon sx={{ color: theme.palette.primary.main }} />,
      'Dodatkowe informacje': <DescriptionIcon sx={{ color: theme.palette.primary.main }} />,
      SEO: <SearchIcon sx={{ color: theme.palette.primary.main }} />,
      Konfiguracja: <SettingsIcon sx={{ color: theme.palette.primary.main }} />,
    },
  });

  const createLookupSearchHandler = useCallback(
    (fieldName: OfferLookupField) =>
      (inputValue: string, reason?: 'input' | 'clear' | 'open') => {
        if (reason === 'open') {
          return;
        }

        setOfferLookupSearches((prev) => ({
          ...prev,
          [fieldName]: reason === 'clear' ? '' : inputValue,
        }));
      },
    []
  );

  const handleChangeWithLookupModal = useCallback(
    (event: any, child?: React.ReactNode) => {
      const { name, value } = event?.target ?? {};

      if (
        typeof name === 'string' &&
        OFFER_LOOKUP_FIELDS.includes(name as OfferLookupField) &&
        value === NEW_OFFER_LOOKUP_OPTION_VALUE
      ) {
        setAutocompleteResetKeys((prev) => ({
          ...prev,
          [name]: (prev[name] ?? 0) + 1,
        }));
        setActiveLookupField(name as OfferLookupField);
        setAddLookupModalOpen(true);
        return;
      }

      entityForm.formHandlers.onChange(event, child);
    },
    [entityForm.formHandlers]
  );

  const handleLookupCreated = useCallback(
    (payload: {
      field: OfferLookupField;
      id: number;
      label: string;
      relatedCountryId?: number;
    }) => {
      setAdditionalLookupOptions((prev) => ({
        ...prev,
        [payload.field]: mergeLookupOptions(prev[payload.field], [
          { label: payload.label, value: payload.id },
        ]),
      }));
      setOfferLookupSearches((prev) => ({
        ...prev,
        [payload.field]: '',
      }));

      if (payload.field === 'regionId' && payload.relatedCountryId) {
        entityForm.formHandlers.setFieldValue('countryId', payload.relatedCountryId);
      }

      entityForm.formHandlers.setFieldValue(payload.field, payload.id);
      setAddLookupModalOpen(false);
    },
    [entityForm.formHandlers]
  );

  const optionTypes = (offerOptionTypes || []).map((opt) => ({
    ...opt,
    offerOptionsValues: (opt.offerOptionsValues || []).map((val) => ({
      ...val,
      value: val.value ?? '',
    })),
  }));

  const selectedCountryId = Number(entityForm.formState.formData.countryId);
  const defaultCountryId =
    Number.isInteger(selectedCountryId) && selectedCountryId > 0 ? selectedCountryId : undefined;

  return (
    <>
      <AddOfferLookupModal
        open={addLookupModalOpen}
        field={activeLookupField}
        initialName={offerLookupSearches[activeLookupField]}
        defaultCountryId={defaultCountryId}
        countries={countryLookupOptions}
        onClose={() => setAddLookupModalOpen(false)}
        onCreated={handleLookupCreated}
      />
      <EntityFormLayout
        {...entityForm}
        formHandlers={{
          ...entityForm.formHandlers,
          onChange: handleChangeWithLookupModal,
        }}
        customFilterOptions={{
          countryId: (options, state) =>
            filterOfferLookupWithAddNew(options, state, 'nowy kraj'),
          regionId: (options, state) =>
            filterOfferLookupWithAddNew(options, state, 'nowy region'),
          hotelId: (options, state) =>
            filterOfferLookupWithAddNew(options, state, 'nowy hotel'),
        }}
        autocompleteInputChangeHandlers={{
          countryId: createLookupSearchHandler('countryId'),
          regionId: createLookupSearchHandler('regionId'),
          hotelId: createLookupSearchHandler('hotelId'),
        }}
        autocompleteResetKeys={autocompleteResetKeys}
      >
        <AccordionCard title="Opcje oferty">
          <OfferOptionMappingsSection
            offerId={createdOfferId}
            offerOptionTypes={optionTypes}
            mappings={mappings || []}
            mappingsLoading={mappingsLoading}
            offerOptionsLoading={offerOptionsLoading}
            refetchMappings={refetchMappings}
          />
        </AccordionCard>
        <AccordionCard
          title={'Miejsca wsiadania'}
          icon={<LocalOfferIcon fontSize="large" color="primary" />}
        >
          <OfferBoardingPlacesSection offerId={createdOfferId} />
        </AccordionCard>
        <AccordionCard
          title={'Program oferty'}
          icon={<EventNoteIcon fontSize="large" color="primary" />}
        >
          {!createdOfferId ? (
            <Typography color="text.secondary">
              Program oferty jest dostępny po zapisaniu oferty.
            </Typography>
          ) : (
            <OfferProgramTable offerId={createdOfferId} />
          )}
        </AccordionCard>
        <AccordionCard
          title={
            showBookingsTable
              ? 'Lista automatycznych rezerwacji'
              : 'Stwórz automatycznie rezerwacje'
          }
          icon={<BookOnlineIcon fontSize="large" color="primary" />}
        >
          {!createdOfferId ? (
            <Typography color="text.secondary">
              Automatyczne rezerwacje są dostępne po zapisaniu oferty.
            </Typography>
          ) : showBookingsTable ? (
            <OfferBookingsTable offerId={createdOfferId} />
          ) : (
            <AccordionCardWithDialogButton
              buttonLabel="Stwórz automatyczne rezerwacje"
              DialogComponent={AutomaticBookingsDialog}
              dialogProps={{
                offerId: createdOfferId,
                onSuccess: () => setShowBookingsTable(true),
              }}
            />
          )}
        </AccordionCard>
      </EntityFormLayout>
    </>
  );
};

export default CreateOfferPage;
